import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  EyeIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import * as KPICard from '../components/KPICard';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getConversionRate from '@dashboard/services/getConversionRate';
import EventTicketSales from '../components/EventTicketSales';
import { useQuery } from '@tanstack/react-query';
import { eventDashboardOverviewKey } from '@/dashboard/lib/queryKeys';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import Spinner from '@/dashboard/components/Spinner';
import PageOverviewHeader from '../components/PageOverviewHeader';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import formatRecoveredStats from '@/dashboard/services/abandonment/formatRecoveredStats';
import getRecoveredTooltipText from '@/dashboard/services/abandonment/getRecoveredTooltipText';

export default function CheckoutOverview() {
  const { page } = usePage();
  const { seller } = useAuth();
  const { eventDetails } = page;
  const { getEventOverview } = useSellerQueries();

  const conversionRate = getConversionRate(page.bookingCount, page.visitCount);

  const { data: eventDashboardOverviewData, isPending: eventDashboardOverviewIsLoading } = useQuery(
    {
      queryKey: eventDashboardOverviewKey(page._id),
      queryFn: () => getEventOverview({ sellerId: page.seller?._id, pageId: page._id }),
      enabled: page.seller?._id != null && page._id != null,
    },
  );

  return (
    <div>
      <PageOverviewHeader page={page}>
        <KPICard.Container>
          <KPICard.Icon icon={ChartBarIcon} />
          <KPICard.Header>
            <KPICard.Title>Revenue</KPICard.Title>
            <KPICard.InfoTooltip>Gross revenue before refunds and Stripe fees</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {eventDashboardOverviewIsLoading == false ? (
              <>
                {getPriceLabel({
                  amount: eventDashboardOverviewData?.totalAmount,
                  currency: eventDetails.currency,
                })}
              </>
            ) : (
              <Spinner size="small" />
            )}
          </KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={TicketIcon} />
          <KPICard.Header>
            <KPICard.Title>Tickets sold</KPICard.Title>
            <KPICard.InfoTooltip>
              Total number of tickets sold out of max capacity
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {eventDashboardOverviewIsLoading == false ? (
              <>
                {eventDashboardOverviewData?.totalTicketsSold}
                {eventDashboardOverviewData?.totalCapacity != null &&
                  `/${eventDashboardOverviewData?.totalCapacity}`}
              </>
            ) : (
              <Spinner size="small" />
            )}
          </KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={EyeIcon} />
          <KPICard.Header>
            <KPICard.Title>Page views</KPICard.Title>
            <KPICard.InfoTooltip>Total number of event page views</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>{page.visitCount}</KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={ArrowTrendingUpIcon} />
          <KPICard.Header>
            <KPICard.Title>Conversion rate</KPICard.Title>
            <KPICard.InfoTooltip>Conversion Rate (conversions ÷ views × 100)</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>{conversionRate}%</KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={ArrowPathIcon} />
          <KPICard.Header>
            <KPICard.Title>Recovered</KPICard.Title>
            <KPICard.InfoTooltip>
              {getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {eventDashboardOverviewIsLoading == false ? (
              formatRecoveredStats({
                totalRecovered: eventDashboardOverviewData?.abandonedCartStats?.totalRecovered,
                totalAbandoned: eventDashboardOverviewData?.abandonedCartStats?.totalAbandoned,
                totalRecoveredRevenue:
                  eventDashboardOverviewData?.abandonedCartStats?.totalRecoveredRevenue,
                currency: page.eventDetails?.currency,
                showRevenue: seller.checkoutAbandonment?.showRecovered,
              })
            ) : (
              <Spinner size="small" />
            )}
          </KPICard.Value>
        </KPICard.Container>
      </PageOverviewHeader>

      <div className="mt-8 mb-16">
        {eventDashboardOverviewIsLoading ? (
          <Spinner />
        ) : (
          <EventTicketSales ticketGroups={eventDashboardOverviewData?.ticketGroups} page={page} />
        )}
      </div>
    </div>
  );
}
